import React, { useEffect, useRef, useState } from "react";
import {
    Box, Grid, Typography, Button, TextField, Alert, CircularProgress,
    Chip, IconButton,
} from "@mui/material";
import {
    QrCodeScanner as ScannerIcon,
    Edit as EditIcon,
    PhotoCamera as CameraIcon,
    Refresh as RefreshIcon,
    CloudUpload as UploadIcon,
    CheckCircle as CheckIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import jsPDF from "jspdf";
import ScannerNew from "../../../components/common/ScannerNew";
import CommonLoader from "../common/CommonLoader";
import {
    AttachCoc as AttachCocApi,
    LookupOrderForCoc,
} from "../../services/ChainOfCustodyService";

const STEP = {
    CHOOSE: "choose",
    SCAN: "scan",
    CONFIRM: "confirm",
    CAPTURE: "capture",
    PREVIEW: "preview",
    SUBMITTING: "submitting",
    DONE: "done",
};

function extractOrderNumber(scanText) {
    if (!scanText) return null;
    const text = scanText.toString().trim();
    const labeled = text.match(/Order Number:\s*(\d{10})/i);
    if (labeled) return labeled[1];
    const leading = text.match(/^(\d{10})\b/);
    if (leading) return leading[1];
    const anywhere = text.match(/\b(\d{10})\b/);
    if (anywhere) return anywhere[1];
    return null;
}


function buildPdfFromPages(pages, orderNumber) {
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
    const pageWidthPt = pdf.internal.pageSize.getWidth();
    const pageHeightPt = pdf.internal.pageSize.getHeight();
    const margin = 24;
    const maxW = pageWidthPt - margin * 2;
    const maxH = pageHeightPt - margin * 2;

    pages.forEach((p, idx) => {
        if (idx > 0) pdf.addPage();
        const scale = Math.min(maxW / p.width, maxH / p.height);
        const drawW = p.width * scale;
        const drawH = p.height * scale;
        const x = (pageWidthPt - drawW) / 2;
        const y = (pageHeightPt - drawH) / 2;
        pdf.addImage(p.dataUrl, "JPEG", x, y, drawW, drawH);
    });

    const blob = pdf.output("blob");
    return new File([blob], `${orderNumber}.pdf`, { type: "application/pdf" });
}

const AttachCoc = () => {
    const [step, setStep] = useState(STEP.CHOOSE);
    const [orderNumber, setOrderNumber] = useState("");
    const [manualEntry, setManualEntry] = useState(false);
    const [orderInfo, setOrderInfo] = useState(null);
    const [lookingUp, setLookingUp] = useState(false);
    const [lookupError, setLookupError] = useState("");

    // Multi-page state: each page is { dataUrl, url, width, height }
    // dataUrl is the base64 JPEG (used by jsPDF); url is an object URL for <img> preview.
    const [pages, setPages] = useState([]);

    const [errorMsg, setErrorMsg] = useState("");
    const [result, setResult] = useState(null);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const [cameraStarting, setCameraStarting] = useState(false);
    const [cameraError, setCameraError] = useState("");

    const stepLabel = {
        [STEP.CHOOSE]: "Start",
        [STEP.SCAN]: "Scan QR / Barcode",
        [STEP.CONFIRM]: "Confirm Order Number",
        [STEP.CAPTURE]: pages.length === 0 ? "Take Photo" : `Take Photo — Page ${pages.length + 1}`,
        [STEP.PREVIEW]: "Review Pages",
        [STEP.SUBMITTING]: "Uploading",
        [STEP.DONE]: "Done",
    }[step];

    // Revoke object URLs when pages unmount
    const revokeAllPageUrls = (pageList) => {
        pageList.forEach((p) => {
            try { URL.revokeObjectURL(p.url); } catch { /* noop */ }
        });
    };

    const resetAll = () => {
        revokeAllPageUrls(pages);
        setStep(STEP.CHOOSE);
        setOrderNumber("");
        setManualEntry(false);
        setOrderInfo(null);
        setLookingUp(false);
        setLookupError("");
        setPages([]);
        setErrorMsg("");
        setResult(null);
    };

    const validateOrderNumber = (num) => /^\d{10}$/.test((num || "").trim());

    const doLookup = async (num) => {
        setLookingUp(true);
        setLookupError("");
        try {
            const resp = await LookupOrderForCoc(num);
            const info = resp?.results;
            if (info?.found) {
                setOrderInfo(info);
            } else {
                setOrderInfo(null);
                setLookupError(`No order found matching ${num}. Verify the number is correct.`);
            }
        } catch {
            setOrderInfo(null);
            setLookupError("Could not verify order — you can still proceed if you're sure the number is correct.");
        } finally {
            setLookingUp(false);
        }
    };

    const handleScan = (scanText) => {
        const extracted = extractOrderNumber(scanText);
        if (extracted) {
            setOrderNumber(extracted);
            setManualEntry(false);
            setStep(STEP.CONFIRM);
            doLookup(extracted);
        } else {
            NotificationManager.warning(
                "Couldn't find a 10-digit order number in the scanned code. Please enter it manually.",
                "Manual Entry Required",
                6000
            );
            setOrderNumber("");
            setManualEntry(true);
            setStep(STEP.CONFIRM);
        }
    };

    const startManualEntry = () => {
        setManualEntry(true);
        setOrderNumber("");
        setStep(STEP.CONFIRM);
    };

    const handleConfirmOrder = () => {
        if (!validateOrderNumber(orderNumber)) {
            setErrorMsg("Order number must be exactly 10 digits.");
            return;
        }
        setErrorMsg("");
        setStep(STEP.CAPTURE);
    };

    // ── Camera ──
    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) videoRef.current.srcObject = null;
    };

    const startCamera = async () => {
        stopCamera();
        setCameraError("");
        setCameraStarting(true);
        try {
            if (!navigator.mediaDevices?.getUserMedia) {
                throw new Error("Camera API not available in this browser.");
            }
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: "environment" },
                    width: { ideal: 1920 },
                    height: { ideal: 1440 },
                },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => {});
            }
        } catch (err) {
            const name = err?.name || "";
            setCameraError(
                name === "NotAllowedError" || name === "PermissionDeniedError"
                    ? "Camera permission denied. Please allow camera access in your browser and try again."
                    : name === "NotFoundError" || name === "DevicesNotFoundError"
                        ? "No camera detected on this device."
                        : name === "NotReadableError"
                            ? "Camera is in use by another application. Close it and try again."
                            : (err?.message || "Could not access the camera.")
            );
        } finally {
            setCameraStarting(false);
        }
    };

    useEffect(() => {
        if (step === STEP.CAPTURE) startCamera();
        else stopCamera();
        return () => stopCamera();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const capturePage = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || !video.videoWidth) return;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        canvas.toBlob(
            (blob) => {
                if (!blob) return;
                setPages((prev) => [
                    ...prev,
                    {
                        dataUrl,
                        url: URL.createObjectURL(blob),
                        width: canvas.width,
                        height: canvas.height,
                    },
                ]);
                setStep(STEP.PREVIEW);
            },
            "image/jpeg",
            0.92
        );
    };

    const removePage = (index) => {
        setPages((prev) => {
            const removed = prev[index];
            if (removed) {
                try { URL.revokeObjectURL(removed.url); } catch { /* noop */ }
            }
            const next = prev.filter((_, i) => i !== index);
            if (next.length === 0) setStep(STEP.CAPTURE);
            return next;
        });
    };

    const addAnotherPage = () => setStep(STEP.CAPTURE);

    // ── Submit ──
    const submit = async () => {
        if (!validateOrderNumber(orderNumber) || pages.length === 0) return;
        setStep(STEP.SUBMITTING);
        setErrorMsg("");
        try {
            const pdfFile = buildPdfFromPages(pages, orderNumber.trim());
            const response = await AttachCocApi(pdfFile, orderNumber.trim());
            const payload = response?.results ?? response;
            if (payload?.success === false) {
                setErrorMsg(payload.message || "Attach failed.");
                setStep(STEP.PREVIEW);
                return;
            }
            setResult({
                orderNumber: payload?.orderNumber || orderNumber.trim(),
                fileName: payload?.fileName,
                message: payload?.message,
                pageCount: pages.length,
            });
            setStep(STEP.DONE);
            NotificationManager.success(
                `CoC attached to order ${payload?.orderNumber || orderNumber.trim()}`,
                "Success"
            );
        } catch (err) {
            const serverDetail = err?.serverMessage || "";
            const statusPart = err?.response?.status ? ` (HTTP ${err.response.status})` : "";
            setErrorMsg(
                serverDetail
                    ? `Attach failed${statusPart}: ${serverDetail}`
                    : (err?.response?.status
                        ? `Attach failed${statusPart}. Try again.`
                        : "Attach failed. Check your connection and try again.")
            );
            setStep(STEP.PREVIEW);
        }
    };

    // ── Renderers ──

    const renderChoose = () => (
        <Grid container spacing={2} sx={{ maxWidth: 600 }}>
            <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                    Attach a Chain of Custody document to an order. Scan the QR/barcode on the paper CoC, then take a photo of each page.
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Button
                    fullWidth variant="contained" color="primary" size="medium"
                    startIcon={<ScannerIcon />}
                    onClick={() => setStep(STEP.SCAN)}
                    style={{ height: "40px" }}
                >
                    Scan QR / Barcode
                </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Button
                    fullWidth variant="outlined" color="primary" size="medium"
                    startIcon={<EditIcon />}
                    onClick={startManualEntry}
                    style={{ height: "40px" }}
                >
                    Enter Order # Manually
                </Button>
            </Grid>
        </Grid>
    );

    const renderScan = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <ScannerNew onScan={handleScan} />
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Button size="small" onClick={resetAll}>Back</Button>
                    <Button size="small" startIcon={<EditIcon />} onClick={startManualEntry}>
                        Can't scan? Enter manually
                    </Button>
                </Box>
            </Grid>
        </Grid>
    );

    const renderConfirm = () => (
        <Grid container spacing={2} sx={{ maxWidth: 700 }}>
            <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                    {manualEntry
                        ? "Enter the 10-digit order number from the Chain of Custody document."
                        : "Verify the order number extracted from the scan. Edit if needed."}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    label="Order Number"
                    value={orderNumber}
                    onChange={(e) => {
                        setOrderNumber(e.target.value.replace(/\D/g, "").slice(0, 10));
                        setOrderInfo(null);
                        setLookupError("");
                        setErrorMsg("");
                    }}
                    onBlur={() => validateOrderNumber(orderNumber) && doLookup(orderNumber)}
                    fullWidth size="small" variant="outlined"
                    autoFocus={manualEntry}
                    inputProps={{ inputMode: "numeric", maxLength: 10 }}
                    error={!!errorMsg}
                    helperText={errorMsg || (validateOrderNumber(orderNumber) ? " " : "10 digits required")}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", height: 40 }}>
                    {lookingUp && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">Verifying order...</Typography>
                        </Box>
                    )}
                </Box>
            </Grid>
            {orderInfo && (
                <Grid item xs={12}>
                    <Alert severity="success" icon={<CheckIcon fontSize="inherit" />}>
                        <Typography variant="body2" fontWeight={600} component="span">
                            {orderInfo.clientName}
                        </Typography>
                        <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                            · Account #{orderInfo.accountNumber}
                        </Typography>
                        {orderInfo.status && <Chip label={orderInfo.status} size="small" sx={{ ml: 1 }} />}
                    </Alert>
                </Grid>
            )}
            {lookupError && !orderInfo && (
                <Grid item xs={12}><Alert severity="warning">{lookupError}</Alert></Grid>
            )}
            <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1 }}>
                    <Button size="small" onClick={resetAll}>Start Over</Button>
                    <Button
                        variant="contained" color="primary" size="medium"
                        startIcon={<CameraIcon />}
                        disabled={!validateOrderNumber(orderNumber)}
                        onClick={handleConfirmOrder}
                        style={{ height: "40px" }}
                    >
                        Proceed to Take Photo
                    </Button>
                </Box>
            </Grid>
        </Grid>
    );

    const renderCapture = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Alert severity="info">
                    <Typography variant="body2">
                        Attaching to order <strong>{orderNumber}</strong>
                        {orderInfo && <> — {orderInfo.clientName}</>}
                        {pages.length > 0 && <> · Capturing page {pages.length + 1}</>}
                    </Typography>
                </Alert>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                    {pages.length === 0
                        ? "Position the paper Chain of Custody in frame. Ensure the whole page is visible and the text is readable, then click Capture."
                        : `Position the next page in frame, then click Capture. You've captured ${pages.length} page${pages.length === 1 ? "" : "s"} so far.`}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Box
                    sx={{
                        position: "relative",
                        width: "100%", maxWidth: 480, mx: "auto", my: 1,
                        backgroundColor: "#000",
                        borderRadius: 1, overflow: "hidden",
                        aspectRatio: "4 / 3",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: 1, borderColor: "divider",
                    }}
                >
                    <video
                        ref={videoRef} playsInline muted autoPlay
                        style={{
                            width: "100%", height: "100%", objectFit: "contain",
                            display: cameraError ? "none" : "block",
                        }}
                    />
                    {cameraStarting && (
                        <Box sx={{ position: "absolute", color: "white", display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={20} sx={{ color: "white" }} />
                            <Typography variant="body2">Starting camera...</Typography>
                        </Box>
                    )}
                    {cameraError && (
                        <Box sx={{ p: 3, textAlign: "center", color: "white" }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>{cameraError}</Typography>
                            <Button variant="contained" color="primary" size="small" onClick={startCamera}>
                                Retry
                            </Button>
                        </Box>
                    )}
                </Box>
                <canvas ref={canvasRef} style={{ display: "none" }} />
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => setStep(STEP.CONFIRM)}>
                        Edit Order #
                    </Button>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        {pages.length > 0 && (
                            <Button size="small" onClick={() => setStep(STEP.PREVIEW)}>
                                Done — Review ({pages.length})
                            </Button>
                        )}
                        <Button size="small" onClick={resetAll}>Cancel</Button>
                        <Button
                            variant="contained" color="success" size="medium"
                            startIcon={<CameraIcon />}
                            onClick={capturePage}
                            disabled={cameraStarting || !!cameraError}
                            style={{ height: "40px", minWidth: 140 }}
                        >
                            Capture
                        </Button>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );

    const renderPreview = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Alert severity="info">
                    <Typography variant="body2">
                        Attaching to order <strong>{orderNumber}</strong>
                        {orderInfo && <> — {orderInfo.clientName}</>}
                        {" · "}<strong>{pages.length}</strong> page{pages.length === 1 ? "" : "s"}
                    </Typography>
                </Alert>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                    Review each page. Remove any that are blurry or add another page if the CoC is longer.
                </Typography>
            </Grid>

            {pages.map((page, idx) => (
                <Grid item xs={12} sm={6} md={4} key={page.url}>
                    <Box
                        sx={{
                            position: "relative",
                            border: 1, borderColor: "divider", borderRadius: 1,
                            overflow: "hidden", backgroundColor: "grey.100",
                            display: "flex", justifyContent: "center", alignItems: "center",
                            aspectRatio: "3 / 4",
                        }}
                    >
                        <img
                            src={page.url}
                            alt={`CoC page ${idx + 1}`}
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                        />
                        <Box
                            sx={{
                                position: "absolute", top: 4, left: 4,
                                backgroundColor: "rgba(0,0,0,0.65)", color: "white",
                                borderRadius: 1, px: 1, py: 0.25,
                                fontSize: "0.75rem", fontWeight: 600,
                            }}
                        >
                            Page {idx + 1}
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => removePage(idx)}
                            sx={{
                                position: "absolute", top: 4, right: 4,
                                backgroundColor: "rgba(0,0,0,0.65)",
                                color: "white",
                                "&:hover": { backgroundColor: "rgba(200,0,0,0.85)" },
                            }}
                            aria-label={`Remove page ${idx + 1}`}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Grid>
            ))}

            {errorMsg && (
                <Grid item xs={12}><Alert severity="error">{errorMsg}</Alert></Grid>
            )}

            <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Button
                        variant="outlined" color="primary" size="medium"
                        startIcon={<AddIcon />}
                        onClick={addAnotherPage}
                        style={{ height: "40px" }}
                    >
                        Add Another Page
                    </Button>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button size="small" onClick={resetAll}>Cancel</Button>
                        <Button
                            variant="contained" color="success" size="medium"
                            startIcon={<UploadIcon />}
                            onClick={submit}
                            disabled={pages.length === 0}
                            style={{ height: "40px" }}
                        >
                            Submit CoC ({pages.length} page{pages.length === 1 ? "" : "s"})
                        </Button>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );

    const renderDone = () => (
        <Grid container spacing={2} sx={{ maxWidth: 600 }}>
            <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <CheckIcon color="success" sx={{ fontSize: 48 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={600}>CoC Attached</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Order <strong>{result?.orderNumber}</strong>
                            {result?.pageCount && <> · {result.pageCount} page{result.pageCount === 1 ? "" : "s"}</>}
                            {result?.fileName && <> · <code style={{ fontSize: "0.9em" }}>{result.fileName}</code></>}
                        </Typography>
                    </Box>
                </Box>
            </Grid>
            {result?.message && (
                <Grid item xs={12}><Alert severity="info">{result.message}</Alert></Grid>
            )}
            <Grid item xs={12}>
                <Button
                    variant="contained" color="primary" size="medium"
                    onClick={resetAll}
                    style={{ height: "40px" }}
                >
                    Attach Another CoC
                </Button>
            </Grid>
        </Grid>
    );

    return (
        <div className="vlp-component-container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span className="title">Attach Chain of Custody</span>
                {step !== STEP.CHOOSE && step !== STEP.DONE && (
                    <Typography variant="body2" color="text.secondary">
                        Step: {stepLabel}
                    </Typography>
                )}
            </div>
            <hr />

            <Box sx={{ mt: 1 }}>
                {step === STEP.CHOOSE && renderChoose()}
                {step === STEP.SCAN && renderScan()}
                {step === STEP.CONFIRM && renderConfirm()}
                {step === STEP.CAPTURE && renderCapture()}
                {step === STEP.PREVIEW && renderPreview()}
                {step === STEP.DONE && renderDone()}
            </Box>

            <CommonLoader loading={step === STEP.SUBMITTING} />
        </div>
    );
};

export default AttachCoc;
